var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useFormContext } from "react-hook-form";
import moment from "moment";
import { Link, Redirect } from "react-router-dom";
import { DayPickerSingleDateController } from "react-dates";
import classNames from "classnames";
import z from "zod";
import { useShallowCompareEffect } from "react-use";
import ContentLayout from "../../Layout/ContentLayout";
import CustomCard from "../../UI/CustomCard/CustomCard";
import { BasicFormFormat } from "../../UI/FormFormat/FormFormat";
import SearchField from "../../Form/SearchField";
import ServiceResult, { Loaders } from "../../UI/ServiceResult/ServiceResult";
import useService from "../../Hooks/useService";
import { ServiceDeliveryLocationMultiSearch } from "../../Presentational/ServiceDeliveryLocation/ServiceDeliveryLocationSearch";
import { ServiceOfferingSearch } from "../../Presentational/ServiceOffering/ServiceOfferingSearch";
import DateService from "../../../Services/Shared/Date.service";
import ListService from "../../../Services/Shared/List.service";
import FAIcon from "../../UI/FAIcon/FAIcon";
import MeetingAvailabilityService from "../../../Services/MeetingAvailability.service";
import DefaultPopover from "../../UI/Popover/DefaultPopover";
import NumberService from "../../../Services/Shared/Number.service";
import Modal from "../../UI/Modal/Modal";
import ModalHeader from "../../UI/Modal/ModalHeader";
import { HierarchicalTableBody } from "../../UI/HierarchicalTableBody/HierarchicalTableBody";
import ProfilePicture from "../../Presentational/Person/ProfilePicture/ProfilePicture";
import InfoItems from "../../UI/InfoItems/InfoItems";
import { MeetingModalityMultiSearch } from "../../Presentational/MeetingModality/MeetingModalitySearch";
import { AlertError, AlertInfo } from "../../UI/StandardAlerts/StandardAlerts";
import A11yHelpers from "../../../../util/A11yHelpers";
import { PersonWithMeetingAvailabilityMultiSearch } from "../../Presentational/Person/PersonWithMeetingAvailabilitySearch";
import PersonService from "../../../Services/Person.service";
import useParams from "../../Hooks/useParams";
import ServiceOfferingService from "../../../Services/ServiceOffering.service";
import { SaveButton } from "../../UI/StandardButtons/StandardButtons";
import { getMeetingsConfirmationPath } from "./MeetingsRoutes";
import CampaignService from "../../../Services/Campaign.service";
import { LocationMultiSearch } from "../../Presentational/Location/LocationSearch";
import AppointmentCampaignInfoDomainService from "../../../Domain/AppointmentCampaignInfo/AppointmentCampaignInfo.service";
import useHistoryState from "../../Hooks/useHistoryState";
import RoleService from "../../../Services/Role.service";
import { useTermsToRepresent } from "../../Hooks/useTermsToRepresent";
const MEETINGS_SCHEDULE_SEARCH_PARAMS = z.object({
    serviceId: z.coerce.number().optional(),
    // personId
    staffId: z.string().uuid().optional(),
    officeId: z.coerce.number().optional(),
    modalityId: z.coerce.number().optional(),
    locationId: z.coerce.number().optional(),
    // affiliationId, this is provided through the Meetings Tab UI and will be used in the confirmation page
    studentId: z.string().uuid().optional(),
    campaignId: z.coerce.number().optional(),
});
const MeetingSchedulerCalendar = ({ value, onChange, roundDateTo = "startOfDay", highlightedDates, onMonthChange, }) => {
    const [isDateFocused, setIsDateFocused] = React.useState(false);
    const isHighlighted = (day) => {
        return highlightedDates.some((highlightedDate) => day.isSame(highlightedDate, "day"));
    };
    const isBlocked = (day) => {
        return !highlightedDates.some((highlightedDate) => day.isSame(highlightedDate, "day"));
    };
    const isDayHighlighted = (day) => {
        return isHighlighted(day) && !isBlocked(day);
    };
    const isDayBlocked = (day) => {
        return !isHighlighted(day) && isBlocked(day);
    };
    const CustomDatePickerContents = (day) => {
        const isToday = isDayHighlighted(day);
        return (_jsxs(_Fragment, { children: [day.format("D"), isToday && (_jsx("div", { className: "w-100 position-relative", children: _jsx(FAIcon, { icon: "circle", type: "fas", className: classNames("w-100 date-circle"), style: {
                            position: "absolute",
                            fontSize: "0.5rem",
                            left: 0,
                            bottom: -5,
                        } }) }))] }));
    };
    return (_jsx("div", { className: "meeting-scheduler", children: _jsx(DayPickerSingleDateController, { date: value, initialVisibleMonth: () => moment(), onNextMonthClick: (mom) => {
                onMonthChange(mom.month() + 1, mom.year());
            }, onPrevMonthClick: (mom) => {
                // Prevent going to months entirely in the past
                const targetMonthEnd = moment(mom).endOf("month");
                const today = moment().startOf("day");
                if (targetMonthEnd.isBefore(today)) {
                    return; // Don't navigate to past months
                }
                onMonthChange(mom.month() + 1, mom.year());
            }, onDateChange: (newDate) => {
                var _a, _b;
                if (!newDate) {
                    return onChange(null);
                }
                switch (roundDateTo) {
                    case "startOfDay":
                        return onChange((_a = moment(newDate)) === null || _a === void 0 ? void 0 : _a.startOf("day"));
                    case "endOfDay":
                        return onChange((_b = moment(newDate)) === null || _b === void 0 ? void 0 : _b.endOf("day"));
                    default:
                        return onChange(moment(newDate));
                }
            }, focused: isDateFocused !== null && isDateFocused !== void 0 ? isDateFocused : false, onFocusChange: ({ focused }) => setIsDateFocused(focused), numberOfMonths: 1, isDayBlocked: isDayBlocked, renderDayContents: CustomDatePickerContents, hideKeyboardShortcutsPanel: true }) }));
};
// Grab all params from the url and parse them into matching values for the form state
const parseSearchParamsToDefaultValues = (meetingsScheduleParams) => __awaiter(void 0, void 0, void 0, function* () {
    const { staffId, serviceId } = meetingsScheduleParams;
    const [service, staff] = yield Promise.all([
        serviceId ? ServiceOfferingService.get(serviceId) : null,
        staffId
            ? PersonService.searchWithMeetingAvailability({
                ids: [staffId],
            })
            : null,
    ]);
    return {
        offering: service,
        staff,
        locations: null,
        offices: null,
        modalities: null,
    };
});
const MeetingDependencyForm = ({ initialDependencies, setDependencyErrorMessage, saveButtonRef, slotsState, isCurrentUserStaff, termToRepresentAdvisor, termToRepresentCoach, scheduleRestrictionBypassed, bypassedForServiceId, currentStudent, isFromCampaign, onClear, }) => {
    var _a;
    const { watch, reset, setValue } = useFormContext();
    const staff = watch("staff");
    const offering = watch("offering");
    const offices = watch("offices");
    const campuses = watch("locations");
    const modalities = watch("modalities");
    const currentDependencies = React.useMemo(() => initialDependencies.search({
        people: staff && staff.length ? staff.map((s) => s.personId) : null,
        offerings: (offering === null || offering === void 0 ? void 0 : offering.id) ? [offering.id] : null,
        offices: offices && offices.length ? offices.map((o) => o.id) : null,
        campuses: campuses && campuses.length ? campuses.map((c) => c.id) : null,
        modalities: modalities && modalities.length ? modalities.map((m) => m.id) : null,
    }), [campuses, initialDependencies, modalities, offering === null || offering === void 0 ? void 0 : offering.id, offices, staff]);
    const errorMessage = currentDependencies.isInvalid();
    useEffect(() => {
        setDependencyErrorMessage(errorMessage);
    }, [errorMessage, setDependencyErrorMessage]);
    // Determines if we display staff names by checking if the service allows viewing
    // staff names or if the current user is a staff member. This is so staff
    // members can easily schedule meetings for students and allows staff members
    // to bypass the service staff selection restriction.
    // Campaign visits also bypass hide staff names to ensure campaign fulfillment.
    const showStaffNames = ((_a = offering === null || offering === void 0 ? void 0 : offering.showStaffNames) !== null && _a !== void 0 ? _a : true) || isCurrentUserStaff || isFromCampaign;
    const getRestrictedStaffFieldDetails = () => {
        // Staff users are not subject to advisor scheduling restrictions
        if (isCurrentUserStaff) {
            return undefined;
        }
        // Campaign visits bypass all restrictions to ensure campaign fulfillment
        if (isFromCampaign) {
            return undefined;
        }
        // Check if advisor scheduling is required
        if (offering === null || offering === void 0 ? void 0 : offering.requireAdvisorScheduling) {
            // Check if student has any assignable staff (advisor, or coach if allowed)
            // If no assignable staff, treat as no restriction
            const hasAdvisor = !!currentStudent.advisor;
            const hasCoach = offering.allowCoachAsAlternative && !!currentStudent.coach;
            const hasAssignableStaff = hasAdvisor || hasCoach;
            if (!hasAssignableStaff) {
                // Student has no assigned advisor/coach - behave as no restriction
                return undefined;
            }
            // If fallback has been triggered for THIS service, enable staff field
            if (scheduleRestrictionBypassed &&
                bypassedForServiceId === (offering === null || offering === void 0 ? void 0 : offering.id)) {
                return {
                    placeholderText: undefined, // Allow normal staff search
                    helpText: "You can now search slots with any staff.",
                    isBypassed: true, // Flag to indicate field should be enabled
                };
            }
            // Determine placeholder and help text based on coach alternative setting
            if (offering.allowCoachAsAlternative) {
                return {
                    placeholderText: `Your ${termToRepresentAdvisor} or ${termToRepresentCoach}`,
                    helpText: `This service is scheduled with your assigned ${termToRepresentAdvisor.toLowerCase()} or ${termToRepresentCoach.toLowerCase()}.`,
                };
            }
            return {
                placeholderText: `Your ${termToRepresentAdvisor}`,
                helpText: `This service is scheduled with your assigned ${termToRepresentAdvisor.toLowerCase()}.`,
            };
        }
        // If no advisor restriction but hide staff names is enabled
        if (!showStaffNames) {
            return {
                placeholderText: "Name provided upon confirmation",
                helpText: "Staff for this service will be selected randomly from those available in the selected time",
            };
        }
        return undefined;
    };
    return (_jsxs(_Fragment, { children: [_jsx(SearchField, { label: "Service", name: "offering", required: true, as: (provided) => (_createElement(ServiceOfferingSearch, Object.assign({}, provided, { serviceOfferingIdsInclude: currentDependencies.offerings, key: currentDependencies.offerings.join("-"), forceEmptyReturn: !!errorMessage, 
                    // We prompt the user to confirm whether they want to proceed if they pick a new service that has restrictions
                    // If they confirm the action, we update the selected service and clear the staff field
                    // If they cancel it, we set the service back to the value before the selection
                    selectionConfirmation: (newSelection) => {
                        // We need to check for a few scenarios in order to display or not the confirmation modal
                        // The selected service needs to restrict it, the user shouldn't be a staff member
                        // Then we check to see if the staff field is not null (it gets initialized as null)
                        // And if the staff is not an empty array (happens when you clear the field manually)
                        // Schedule restriction supersedes hide staff names, so we check this first
                        // If the restriction type is any other than none, display the confirmation
                        if (newSelection != null &&
                            !isCurrentUserStaff &&
                            !ListService.isNullOrEmpty(staff)) {
                            // Check if it is supposed to hide staff names
                            if (!newSelection.showStaffNames) {
                                return {
                                    message: "Staff for this service are selected randomly and a previous staff selection(s) will be cleared. Are you sure you want to proceed?",
                                    // If user decides to proceed, update the state of the current service clear the status field and update the current field
                                    onConfirmed: () => {
                                        setValue("staff", []);
                                    },
                                };
                            }
                            if (newSelection.requireAdvisorScheduling) {
                                const staffLabel = newSelection.allowCoachAsAlternative
                                    ? `${termToRepresentAdvisor}/${termToRepresentCoach}`
                                    : termToRepresentAdvisor;
                                return {
                                    message: `This service must be scheduled with your assigned ${staffLabel.toLowerCase()}. Your previous staff selection will be cleared.`,
                                    // If user decides to proceed, update the state of the current service clear the status field and update the current field
                                    onConfirmed: () => {
                                        setValue("staff", []);
                                    },
                                };
                            }
                        }
                        return null;
                    } }))) }), _jsxs("div", { className: "row", children: [_jsx("div", { className: "col-6", children: _jsx(SearchField, { label: "Campus", name: "locations", as: (provided) => (_createElement(LocationMultiSearch, Object.assign({}, provided, { ids: currentDependencies.campuses, key: currentDependencies.campuses.join("-"), forceEmptyReturn: !!errorMessage }))) }) }), _jsx("div", { className: "col-6", children: _jsx(SearchField, { label: "Office", name: "offices", as: (provided) => (_createElement(ServiceDeliveryLocationMultiSearch, Object.assign({}, provided, { ids: currentDependencies.offices, key: currentDependencies.offices.join("-"), forceEmptyReturn: !!errorMessage, showInNewScheduler: true }))) }) })] }), _jsxs("div", { className: "row", children: [_jsx("div", { className: "col-6", children: (() => {
                            const staffFieldDetails = getRestrictedStaffFieldDetails();
                            const isDisabled = (!!staffFieldDetails && !staffFieldDetails.isBypassed) ||
                                !showStaffNames;
                            // For advisor-restricted services (not bypassed), filter to only advisor/coach
                            const getStaffIdsForDropdown = () => {
                                var _a, _b;
                                if (staffFieldDetails && !staffFieldDetails.isBypassed) {
                                    const advisorIds = [];
                                    if ((_a = currentStudent.advisor) === null || _a === void 0 ? void 0 : _a.personId) {
                                        advisorIds.push(currentStudent.advisor.personId);
                                    }
                                    if ((offering === null || offering === void 0 ? void 0 : offering.allowCoachAsAlternative) &&
                                        ((_b = currentStudent.coach) === null || _b === void 0 ? void 0 : _b.personId)) {
                                        advisorIds.push(currentStudent.coach.personId);
                                    }
                                    return advisorIds;
                                }
                                return currentDependencies.people;
                            };
                            const staffIds = getStaffIdsForDropdown();
                            return (_jsx(SearchField, { label: "Staff", name: "staff", as: (provided) => (_createElement(PersonWithMeetingAvailabilityMultiSearch, Object.assign({}, provided, { ids: staffIds, key: staffIds.join("-"), placeholder: staffFieldDetails === null || staffFieldDetails === void 0 ? void 0 : staffFieldDetails.placeholderText, forceEmptyReturn: !!errorMessage }))), helpText: staffFieldDetails === null || staffFieldDetails === void 0 ? void 0 : staffFieldDetails.helpText, disabled: isDisabled }));
                        })() }), _jsx("div", { className: "col-6", children: _jsx(SearchField, { label: "How do you want to meet?", name: "modalities", as: (provided) => (_createElement(MeetingModalityMultiSearch, Object.assign({}, provided, { active: true, ids: currentDependencies.modalities, key: currentDependencies.modalities.join("-"), forceEmptyReturn: !!errorMessage }))) }) })] }), (() => {
                // Don't show scheduleHelpText if:
                // 1. User is staff (they bypass restrictions)
                // 2. User is from campaign (campaign bypasses restrictions)
                // 3. User has no assignable staff and service has advisor restriction (restriction doesn't apply)
                // 4. Restriction was bypassed (fallback triggered)
                if (isCurrentUserStaff || !(offering === null || offering === void 0 ? void 0 : offering.scheduleHelpText)) {
                    return null;
                }
                if (isFromCampaign) {
                    return null;
                }
                if (scheduleRestrictionBypassed) {
                    return null;
                }
                if (offering.requireAdvisorScheduling) {
                    const hasAdvisor = !!currentStudent.advisor;
                    const hasCoach = offering.allowCoachAsAlternative && !!currentStudent.coach;
                    const hasAssignableStaff = hasAdvisor || hasCoach;
                    if (!hasAssignableStaff) {
                        return null;
                    }
                }
                return (_jsxs("p", { children: [_jsx(FAIcon, { icon: "info-circle", type: "fas", addMarginRight: true }), " ", offering.scheduleHelpText] }));
            })(), _jsxs("div", { className: "d-flex justify-content-end", children: [_jsx(SaveButton, { isLoading: slotsState.isLoading, onClick: () => { var _a; return (_a = saveButtonRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, overrideLabel: "Find a time", disabled: !!errorMessage }), _jsx("button", { className: "btn btn-outline-secondary ml-2", onClick: () => {
                            reset({
                                staff: [],
                                offices: [],
                                modalities: [],
                                offering: null,
                                locations: [],
                            });
                            onClear();
                        }, children: "Clear" })] })] }));
};
const AppointmentCampaignDetails = ({ appointmentCampaign, }) => {
    return (_jsxs(_Fragment, { children: [_jsx(AlertInfo, { message: _jsxs(_Fragment, { children: ["There are restrictions on scheduling this appointment.", _jsx("button", { className: "btn btn-link p-0 ml-1 alert-link", "data-toggle": "collapse", "data-target": "#appointmentInfo", "aria-expanded": "false", "aria-controls": "collapseExample", children: "More info" })] }) }), _jsx("div", { className: "collapse", id: "appointmentInfo", children: _jsx(CustomCard, { noShadow: true, className: "mb-3", children: _jsx("div", { className: "card-body", children: _jsx("div", { className: "row", children: AppointmentCampaignInfoDomainService.getInfoTextFieldsForMeetingSchedulerAppointmentCampaign(appointmentCampaign).map((info, i) => (_jsx("div", { className: "col-6", children: _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "appointment-info-" + i, children: info.label }), _jsx("div", { id: "appointment-info-" + i, children: info.value })] }) }, i))) }) }) }) })] }));
};
const PickerModal = (_a) => {
    var { slot, service, studentId, showStaffNames } = _a, modalProps = __rest(_a, ["slot", "service", "studentId", "showStaffNames"]);
    if (slot.staffOfficeModalities.length === 1) {
        const slotSingleOption = slot.staffOfficeModalities[0];
        return (_jsx(Redirect, { to: getMeetingsConfirmationPath({
                confirmationSearchParams: {
                    serviceId: service.id,
                    officeId: parseInt(slotSingleOption[1].toString(), 10),
                    staffId: slotSingleOption[0].toString(),
                    startTime: slot.from,
                    endTime: slot.to,
                    modalityId: slotSingleOption[2],
                    studentId,
                },
            }), push: true }));
    }
    return (_jsxs(Modal, Object.assign({}, modalProps, { children: [_jsx(ModalHeader, { title: "Select Meeting" }), _jsxs("div", { className: "modal-body", children: [_jsx("div", { className: "mb-3", children: _jsx(InfoItems, { numColumnsLg: 3, items: [
                                {
                                    label: "Date",
                                    value: DateService.displayLong(slot.from),
                                },
                                {
                                    label: "Time",
                                    value: (_jsxs("div", { children: [DateService.displayJustTime(slot.from), " \u2013", " ", DateService.displayJustTime(slot.to)] })),
                                },
                                { label: "Service", value: service.name },
                            ] }) }), _jsxs("table", { className: "table", children: [_jsx("thead", { children: _jsxs("tr", { children: [showStaffNames && _jsx("th", { children: "Staff" }), _jsx("th", { children: "Location" }), _jsx("th", { children: "How to Meet" })] }) }), _jsx(HierarchicalTableBody, { hiddenColumn: showStaffNames ? undefined : 0, elements: slot.staffOfficeModalities, renderers: [
                                    ({ val: staffId, provided }) => {
                                        const staff = slot.staff.find((s) => s.personId === staffId);
                                        let content;
                                        if (!staff) {
                                            content = "Error finding this staff member";
                                        }
                                        else {
                                            content = (_jsxs("div", { className: "d-flex align-items-center", children: [_jsx(ProfilePicture, { person: staff, size: 30, showProfilePicture: true, className: "mr-2" }), staff.fullName] }));
                                        }
                                        return (_jsx("td", Object.assign({}, provided, { style: { verticalAlign: "middle" }, children: content })));
                                    },
                                    ({ val: officeId, provided }) => {
                                        const office = slot.offices.find((o) => o.id === officeId);
                                        let content;
                                        if (!office) {
                                            content = "Error finding this office";
                                        }
                                        else {
                                            content = `${office.location.name} - ${office.name}`;
                                        }
                                        return (_jsx("td", Object.assign({}, provided, { style: { verticalAlign: "middle" }, children: content })));
                                    },
                                    ({ val: modalityId, provided, otherOptions }) => {
                                        const modality = slot.modalities.find((m) => m.id === modalityId);
                                        let content;
                                        if (!modality || !otherOptions) {
                                            content = `Error finding this way of meeting ${modalityId}`;
                                        }
                                        else {
                                            content = (_jsxs("div", { className: "d-flex align-items-center justify-content-between", children: [modality.name, _jsx(Link, { to: getMeetingsConfirmationPath({
                                                            confirmationSearchParams: {
                                                                serviceId: service.id,
                                                                officeId: parseInt(otherOptions[1].toString(), 10),
                                                                staffId: ListService.getRandomFromList(otherOptions[0]).toString(),
                                                                startTime: slot.from,
                                                                endTime: slot.to,
                                                                modalityId,
                                                                studentId,
                                                            },
                                                        }), className: "btn btn-sm btn-primary", children: "Select" })] }));
                                        }
                                        return (_jsx("td", Object.assign({}, provided, { style: { verticalAlign: "middle", minWidth: 170 }, children: content })));
                                    },
                                ] })] })] })] })));
};
const SLOT_HEADERS = {
    Morning: [0, 12],
    Afternoon: [12, 16],
    Evening: [16, 24],
};
const Slots = ({ slots, emptyMessage, service, studentId, formSelections, isStaff, scheduleRestrictionBypassed, bypassedForServiceId, setBypassedForServiceId, serviceWithAvailability, setServiceWithAvailability, setFormSelections, currentStudent, isFromCampaign, termToRepresentAdvisor, termToRepresentCoach, availabilityCheckDone, hasAvailabilityInFallbackWindow, setDate, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const slotsGroupedByDay = ListService.groupByString(slots, (s) => { var _a; return (_a = DateService.displayLong(DateService.getStartOfDay(s.from))) !== null && _a !== void 0 ? _a : ""; });
    const [isPickerModalOpen, setIsPickerModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [showFallbackModal, setShowFallbackModal] = useState(false);
    // Reset modal when service changes
    useEffect(() => {
        setShowFallbackModal(false);
    }, [(_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.id]);
    const fallbackDays = (_c = (_b = formSelections.service) === null || _b === void 0 ? void 0 : _b.fallbackDays) !== null && _c !== void 0 ? _c : 10;
    // Check if advisor has confirmed availability for this service
    // Once slots are found anywhere in the fallback window, this remains true
    const advisorHasAvailability = serviceWithAvailability === ((_d = formSelections.service) === null || _d === void 0 ? void 0 : _d.id);
    // Check if student has any assignable staff (advisor, or coach if allowed)
    const hasAssignableStaff = (() => {
        var _a;
        if (currentStudent.advisor)
            return true;
        if (((_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.allowCoachAsAlternative) && currentStudent.coach)
            return true;
        return false;
    })();
    // Staff users are not subject to advisor scheduling restrictions
    // Don't show fallback if:
    // - User is staff
    // - User is from a campaign (campaign bypasses all restrictions)
    // - Service doesn't require advisor scheduling
    // - Service doesn't allow fallback
    // - Restriction has been bypassed
    // - Advisor has confirmed availability
    // - Student has no assignable staff (no restriction applies)
    const canShowFallbackModal = !isStaff &&
        !isFromCampaign &&
        ((_e = formSelections.service) === null || _e === void 0 ? void 0 : _e.requireAdvisorScheduling) &&
        ((_f = formSelections.service) === null || _f === void 0 ? void 0 : _f.allowFallbackToAllStaff) &&
        !scheduleRestrictionBypassed &&
        !advisorHasAvailability &&
        hasAssignableStaff;
    // Show fallback modal based on availability check result
    // Popup shows ONCE per service (tracked in bypassedServiceIds array)
    // - If service already bypassed → canShowFallbackModal is false → no popup
    // - If advisor has availability → mark it and don't show popup
    // - If no availability in fallback window → show popup once
    useEffect(() => {
        var _a;
        const currentServiceId = (_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.id;
        if (currentServiceId == null)
            return;
        // If service already bypassed or has availability, don't show popup
        if (!canShowFallbackModal)
            return;
        // Wait for availability check to complete before deciding
        if (!availabilityCheckDone)
            return;
        // Availability check is done - use its result
        if (hasAvailabilityInFallbackWindow) {
            setServiceWithAvailability(currentServiceId);
        }
        else if (!showFallbackModal) {
            // No availability in fallback window - show popup (only if not already showing)
            setShowFallbackModal(true);
        }
    }, [
        canShowFallbackModal,
        showFallbackModal,
        (_g = formSelections.service) === null || _g === void 0 ? void 0 : _g.id,
        setServiceWithAvailability,
        availabilityCheckDone,
        hasAvailabilityInFallbackWindow,
    ]);
    // Handle bypass when fallback modal is dismissed (either OK or X button)
    const handleFallbackBypass = () => {
        var _a, _b;
        // Reset calendar to today
        setDate(moment());
        const today = new Date();
        setFormSelections((fs) => (Object.assign(Object.assign({}, fs), { staff: [], monthIndex: today.getMonth() + 1, year: today.getFullYear(), afterDayInMonth: today.getDate() })));
        setBypassedForServiceId((_b = (_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null);
        setShowFallbackModal(false);
    };
    // Fallback modal - use backdrop="static" to prevent closing by clicking outside
    // User can close via OK button or X button - both trigger bypass
    const fallbackModal = (_jsxs(Modal, { isOpen: showFallbackModal, onModalClosed: handleFallbackBypass, size: "md", backdrop: "static", children: [_jsx(ModalHeader, { title: "No Availability Found", closeModal: handleFallbackBypass }), _jsx("div", { className: "modal-body", children: _jsx("p", { children: (() => {
                        var _a;
                        const staffLabel = ((_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.allowCoachAsAlternative)
                            ? `${termToRepresentAdvisor.toLowerCase()}/${termToRepresentCoach.toLowerCase()}`
                            : termToRepresentAdvisor.toLowerCase();
                        return `Your assigned ${staffLabel} does not have availability in the next ${fallbackDays} ${fallbackDays === 1 ? "day" : "days"}. To ensure timely support, you may schedule with another ${staffLabel}.`;
                    })() }) }), _jsx("div", { className: "modal-footer", children: _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleFallbackBypass, children: "OK" }) })] }));
    // Show empty state when no slots available
    // (either no availability, or advisor has no slots and popup is showing)
    if (slots.length === 0) {
        return (_jsxs(_Fragment, { children: [fallbackModal, _jsx("div", { className: "d-flex flex-column justify-content-center align-items-center h-100 w-100 font-weight-bold", children: _jsx("div", { children: emptyMessage }) })] }));
    }
    // Determines if we display staff names by checking if the service allows viewing
    // staff names or if the current user is a staff member. This is so staff
    // members can easily schedule meetings for students and allows staff members
    // to bypass the service staff selection restriction.
    // Campaign visits also bypass hide staff names to ensure campaign fulfillment.
    const showStaffNames = service.showStaffNames || isStaff || isFromCampaign;
    return (_jsxs(_Fragment, { children: [selectedSlot != null && (_jsx(PickerModal, { isOpen: isPickerModalOpen, slot: selectedSlot, onModalClosed: () => setIsPickerModalOpen(false), service: service, size: "lg", studentId: studentId, showStaffNames: showStaffNames })), scheduleRestrictionBypassed &&
                bypassedForServiceId === ((_h = formSelections.service) === null || _h === void 0 ? void 0 : _h.id) && (_jsx(_Fragment, { children: _jsx(AlertInfo, { message: "You can now search slots with any staff." }) })), Object.entries(slotsGroupedByDay).map(([header, groupedSlots], i) => {
                return (_jsxs("div", { className: "mb-4", children: [_jsx("div", { className: "h4 mb-3", children: header }), _jsx("div", { children: Object.entries(SLOT_HEADERS).map(([timeHeader, timeRange], j) => {
                                const slotsInTime = groupedSlots
                                    .filter((slot) => slot.from.getHours() >= timeRange[0] &&
                                    slot.from.getHours() < timeRange[1])
                                    .sort((a, b) => a.from.getTime() - b.from.getTime());
                                if (slotsInTime.length === 0) {
                                    return null;
                                }
                                return (_jsxs("div", { className: "clickable", children: [_jsx("div", { className: "font-weight-200 mt-3 mb-2", children: timeHeader }), _jsx("div", { className: "row", children: slotsInTime.map((slot, si) => (_jsx("div", { className: "col-12 col-md-4 col-lg-3 mb-2", children: _jsx(Slot, { slot: slot, onSelect: () => {
                                                        setSelectedSlot(slot);
                                                        setIsPickerModalOpen(true);
                                                    }, showStaffNames: showStaffNames }) }, si))) })] }, j));
                            }) }), i !== Object.entries(slotsGroupedByDay).length - 1 && _jsx("hr", {})] }, i));
            }), fallbackModal] }));
};
const Slot = ({ slot, onSelect, showStaffNames, }) => (_jsx(DefaultPopover, { popoverBehavior: "hover", isOpen: true, popoverContent: _jsx("div", { children: _jsx("table", { className: "table table-sm mb-0 ", children: _jsxs("tbody", { children: [showStaffNames && (_jsxs("tr", { children: [_jsx("th", { className: "border-top-0", children: "Staff:" }), _jsx("td", { className: "border-top-0", children: ListService.displayWithCommasAndOr(slot.staff.map((p) => p.fullName), 5) })] })), _jsxs("tr", { children: [_jsx("th", { className: classNames({
                                    "border-top-0": !showStaffNames,
                                }), children: "Locations:" }), _jsx("td", { className: classNames({
                                    "border-top-0": !showStaffNames,
                                }), children: _jsxs("ul", { className: "list-group list-group-flush bg-none", children: [slot.offices.slice(0, 3).map((o) => (_jsx("li", { style: { background: "transparent" }, className: "list-group-item p-1", children: `${o.name}, ${o.location.name}` }, o.id))), slot.offices.length > 3 && (_jsxs("li", { style: { background: "transparent" }, className: "list-group-item p-1", children: ["and ", NumberService.format(slot.offices.length - 3), " other", slot.offices.length - 3 === 1 ? "" : "s"] }))] }) })] }), _jsxs("tr", { children: [_jsx("th", { style: { width: 120 }, children: "Ways To Meet:" }), _jsx("td", { children: ListService.displayWithCommasAndOr(slot.modalities.map((m) => m.name), 5) })] })] }) }) }), config: {
        popoverPadding: 2,
        popoverMaxWidth: 400,
        transitionDuration: 100,
    }, children: (provided) => (_jsxs(CustomCard, Object.assign({ tabIndex: 0, role: "button", noShadow: true, className: "rounded-lg meeting-scheduler-slot" }, provided.popoverProps, A11yHelpers.onClickOrEnterOrSpace(() => {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect();
    }), { children: [_jsxs("div", { className: "sr-only", children: [DateService.displayJustTime(slot.from), " to", " ", DateService.displayJustTime(slot.to), ". Available Staff:", " ", ListService.displayWithCommasAndAnd(slot.staff.map((p) => p.fullName), 5), ". Available Locations:", " ", ListService.displayWithCommasAndOr(slot.offices.map((o) => `${o.name}, ${o.location.name}`)), ". Available Ways to Meet:", " ", ListService.displayWithCommasAndOr(slot.modalities.map((m) => m.name), 5)] }), _jsxs("div", { className: "py-2 px-1 d-flex align-items-center justify-content-center", "aria-hidden": true, children: [DateService.displayJustTime(slot.from), _jsxs("small", { className: "text-muted", children: ["\u00A0\u2013 ", DateService.displayJustTime(slot.to)] })] })] }))) }));
const SchedulerContent = (props) => {
    var _a, _b, _c;
    const [formSelections, setFormSelections] = useState({
        staff: [],
        service: null,
        offices: [],
        modalities: [],
        campuses: [],
        monthIndex: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        afterDayInMonth: new Date().getDate(),
        from: null,
        to: null,
    });
    const [historyFormSelections, setHistoryFormSelections] = useHistoryState("formState", {
        modalities: null,
        offering: null,
        offices: null,
        locations: null,
        staff: null,
    });
    // Use useHistoryState to persist bypass states across navigation (back button, refresh)
    // Track which service IDs have been bypassed (popup acknowledged) - persists across service switches
    const [bypassedServiceIds, setBypassedServiceIds] = useHistoryState("bypassedServiceIds", []);
    // Track which service IDs have confirmed advisor availability (slots found)
    const [servicesWithAvailability, setServicesWithAvailability] = useHistoryState("servicesWithAvailability", []);
    // Computed: check if current service is bypassed
    const currentServiceId = (_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.id;
    const scheduleRestrictionBypassed = currentServiceId != null && bypassedServiceIds.includes(currentServiceId);
    const bypassedForServiceId = scheduleRestrictionBypassed
        ? currentServiceId
        : null;
    // Computed: check if current service has availability
    const serviceWithAvailability = currentServiceId != null &&
        servicesWithAvailability.includes(currentServiceId)
        ? currentServiceId
        : null;
    // Add service to bypassed list (called when user clicks OK on popup)
    const addBypassedService = React.useCallback((serviceId) => {
        if (!bypassedServiceIds.includes(serviceId)) {
            setBypassedServiceIds([...bypassedServiceIds, serviceId]);
        }
    }, [bypassedServiceIds, setBypassedServiceIds]);
    // Add service to availability list
    const addServiceWithAvailability = React.useCallback((serviceId) => {
        if (!servicesWithAvailability.includes(serviceId)) {
            setServicesWithAvailability([...servicesWithAvailability, serviceId]);
        }
    }, [servicesWithAvailability, setServicesWithAvailability]);
    // Compatibility wrapper
    const setBypassedForServiceId = React.useCallback((id) => {
        if (id != null) {
            addBypassedService(id);
        }
    }, [addBypassedService]);
    const setServiceWithAvailability = React.useCallback((id) => {
        if (id != null) {
            addServiceWithAvailability(id);
        }
    }, [addServiceWithAvailability]);
    // Track if this is the initial search for an advisor-restricted service
    // Used to trigger availability check on first search
    const isInitialAdvisorSearchRef = React.useRef(true);
    // Reset initial search flag when service changes
    const prevServiceIdForInitialSearchRef = React.useRef(null);
    // Track when we're syncing month to skip redundant API call
    const skipNextFetchRef = React.useRef(false);
    // Track if availability check is in progress or done for current service
    const [availabilityCheckDone, setAvailabilityCheckDone] = useState(false);
    const [hasAvailabilityInFallbackWindow, setHasAvailabilityInFallbackWindow] = useState(false);
    const [slotsState, getSlots] = useService((search, { minDate, maxDate, monthIndex, year, afterDayInMonth, }) => __awaiter(void 0, void 0, void 0, function* () {
        // if a minDate or a maxDate is provided, we switch to specifying dates
        // instead of months, but we still work within the month and year specified
        // in the form selections.
        let timeComponentOfGetSlots = {
            monthIndex,
            afterDayInMonth,
            year,
        };
        if (minDate || maxDate) {
            const startOfMonth = moment(new Date(formSelections.year, monthIndex - 1, 1)).startOf("month");
            const endOfMonth = moment(startOfMonth).endOf("month");
            const calendarIsInRange = (minDate == null || moment(minDate).isBefore(endOfMonth)) &&
                (maxDate == null || moment(maxDate).isAfter(startOfMonth));
            if (!calendarIsInRange) {
                return [];
            }
            const minDateInSelectedMonth = minDate != null &&
                minDate.getMonth() === formSelections.monthIndex - 1;
            const maxDateInSelectedMonth = maxDate != null &&
                maxDate.getMonth() === formSelections.monthIndex - 1;
            if (!minDateInSelectedMonth && !maxDateInSelectedMonth) {
                timeComponentOfGetSlots = {
                    monthIndex: formSelections.monthIndex,
                    afterDayInMonth: formSelections.afterDayInMonth,
                    year: formSelections.year,
                };
            }
            else if (minDateInSelectedMonth && !maxDateInSelectedMonth) {
                timeComponentOfGetSlots = {
                    monthIndex,
                    afterDayInMonth: Math.max(afterDayInMonth !== null && afterDayInMonth !== void 0 ? afterDayInMonth : 0, minDate === null || minDate === void 0 ? void 0 : minDate.getDate()),
                    year: formSelections.year,
                };
            }
            else if (!minDateInSelectedMonth && maxDateInSelectedMonth) {
                // maxDateInSelectedMonth is true only when maxDate != null
                timeComponentOfGetSlots = {
                    from: new Date(year, monthIndex - 1, 0),
                    to: maxDate,
                };
            }
            else {
                // The only option left is if the max and min dates are in the current month.
                // Both minDateInSelectedMonth and maxDateInSelectedMonth are true only when both are non-null
                timeComponentOfGetSlots = {
                    from: minDate,
                    to: maxDate,
                };
            }
        }
        const resp = yield MeetingAvailabilityService.getSlots(Object.assign(Object.assign({}, search), timeComponentOfGetSlots));
        resp.sort((a, b) => a.from.getTime() - b.from.getTime());
        // Set current form selections to the browser history to help with navigation
        setHistoryFormSelections({
            offering: formSelections.service,
            locations: formSelections.campuses,
            offices: formSelections.offices,
            staff: formSelections.staff,
            modalities: formSelections.modalities,
        });
        return resp;
    }));
    // Separate availability check for advisor-restricted services
    // This checks if advisor has ANY slots within the fallback window
    const checkAdvisorAvailability = React.useCallback((search, fallbackDays) => __awaiter(void 0, void 0, void 0, function* () {
        const today = new Date();
        const fallbackEndDate = moment()
            .add(fallbackDays, "days")
            .endOf("day")
            .toDate();
        try {
            const slots = yield MeetingAvailabilityService.getSlots(Object.assign(Object.assign({}, search), { from: today, to: fallbackEndDate }));
            return slots.length > 0;
        }
        catch (_d) {
            return false;
        }
    }), []);
    useShallowCompareEffect(() => {
        var _a, _b, _c;
        if (formSelections.service) {
            // Skip fetch if we're just syncing month (calendar sync)
            if (skipNextFetchRef.current) {
                skipNextFetchRef.current = false;
                return;
            }
            // Check if service changed - reset flags
            const serviceId = formSelections.service.id;
            const serviceChanged = prevServiceIdForInitialSearchRef.current !== serviceId;
            // Check if this is a restricted service that was already bypassed
            const isAlreadyBypassedRestrictedService = serviceChanged &&
                !props.isCurrentUserStaff &&
                !((_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.campaignId) &&
                formSelections.service.requireAdvisorScheduling &&
                formSelections.service.allowFallbackToAllStaff &&
                scheduleRestrictionBypassed;
            if (serviceChanged) {
                prevServiceIdForInitialSearchRef.current = serviceId;
                // Don't set isInitialAdvisorSearchRef for already-bypassed services
                // This ensures we skip availability check and go straight to all-staff search
                if (!isAlreadyBypassedRestrictedService) {
                    isInitialAdvisorSearchRef.current = true;
                }
                setAvailabilityCheckDone(false);
                setHasAvailabilityInFallbackWindow(false);
            }
            // Check if student has assignable staff
            const hasAdvisor = !!props.currentStudent.advisor;
            const hasCoach = !!formSelections.service.allowCoachAsAlternative &&
                !!props.currentStudent.coach;
            const hasAssignableStaff = hasAdvisor || hasCoach;
            // Check if from campaign (campaigns bypass all restrictions)
            const isFromCampaign = !!((_b = props.searchParams) === null || _b === void 0 ? void 0 : _b.campaignId);
            // Determine if this is an advisor-restricted service with fallback
            const isAdvisorRestrictedWithFallback = !props.isCurrentUserStaff &&
                !isFromCampaign &&
                !!formSelections.service.requireAdvisorScheduling &&
                !!formSelections.service.allowFallbackToAllStaff &&
                !scheduleRestrictionBypassed &&
                hasAssignableStaff;
            // Get advisor/coach staff IDs for restricted services
            const getAdvisorStaffIds = () => {
                var _a, _b, _c;
                const advisorIds = [];
                if ((_a = props.currentStudent.advisor) === null || _a === void 0 ? void 0 : _a.personId) {
                    advisorIds.push(props.currentStudent.advisor.personId);
                }
                if (((_b = formSelections.service) === null || _b === void 0 ? void 0 : _b.allowCoachAsAlternative) &&
                    ((_c = props.currentStudent.coach) === null || _c === void 0 ? void 0 : _c.personId)) {
                    advisorIds.push(props.currentStudent.coach.personId);
                }
                return advisorIds;
            };
            // Check if this is a bypassed restricted service (regardless of service change)
            const isBypassedRestrictedService = !props.isCurrentUserStaff &&
                !isFromCampaign &&
                formSelections.service.requireAdvisorScheduling &&
                formSelections.service.allowFallbackToAllStaff &&
                scheduleRestrictionBypassed &&
                hasAssignableStaff;
            // Determine staff IDs based on context
            const getStaffIds = () => {
                // If user selected specific staff, always use that (highest priority)
                if (ListService.returnNullIfEmpty(formSelections.staff)) {
                    return formSelections.staff.map((s) => s.personId);
                }
                // For bypassed restricted services (no staff selected), use all staff
                if (isBypassedRestrictedService) {
                    return props.dependencies.people;
                }
                // For advisor-restricted services (not bypassed), use only advisor/coach
                if (isAdvisorRestrictedWithFallback) {
                    return getAdvisorStaffIds();
                }
                // Otherwise use all available staff
                return props.dependencies.people;
            };
            // Build search params
            const searchParams = {
                serviceOfferingId: formSelections.service.id,
                serviceDeliveryLocationIds: ListService.returnNullIfEmpty(formSelections.offices)
                    ? formSelections.offices.map((o) => o.id)
                    : props.dependencies.offices,
                staffIds: getStaffIds(),
                meetingModalityIds: ListService.returnNullIfEmpty(formSelections.modalities)
                    ? formSelections.modalities.map((m) => m.id)
                    : props.dependencies.modalities,
                campusIds: ListService.returnNullIfEmpty(formSelections.campuses)
                    ? formSelections.campuses.map((c) => c.id)
                    : props.dependencies.campuses,
            };
            // For advisor-restricted services with fallback: check availability first
            // Only search if advisor has availability, otherwise show popup
            if (isAdvisorRestrictedWithFallback &&
                isInitialAdvisorSearchRef.current) {
                isInitialAdvisorSearchRef.current = false;
                const fallbackDays = (_c = formSelections.service.fallbackDays) !== null && _c !== void 0 ? _c : 10;
                // Store params for use in callback
                const slotSearchParams = {
                    minDate: props.minDate,
                    maxDate: props.maxDate,
                    monthIndex: formSelections.monthIndex,
                    afterDayInMonth: formSelections.afterDayInMonth,
                    year: formSelections.year,
                };
                // Check availability first, then search
                checkAdvisorAvailability(searchParams, fallbackDays).then((hasAvailability) => {
                    // Use flushSync to ensure state updates are applied before getSlots
                    // This ensures Slots component receives correct props when it renders
                    flushSync(() => {
                        setHasAvailabilityInFallbackWindow(hasAvailability);
                        setAvailabilityCheckDone(true);
                        if (hasAvailability) {
                            setServiceWithAvailability(serviceId);
                        }
                    });
                    // Now call getSlots - Slots component will have correct props
                    getSlots(searchParams, slotSearchParams);
                });
                return; // Don't search yet, availability check will call getSlots
            }
            // For non-advisor-restricted services OR bypassed - search normally
            getSlots(searchParams, {
                minDate: props.minDate,
                maxDate: props.maxDate,
                monthIndex: formSelections.monthIndex,
                afterDayInMonth: formSelections.afterDayInMonth,
                year: formSelections.year,
            });
        }
    }, [
        formSelections,
        getSlots,
        checkAdvisorAvailability,
        props.dependencies.modalities,
        props.dependencies.offices,
        props.dependencies.people,
        props.maxDate,
        props.minDate,
        props.isCurrentUserStaff,
        (_b = props.searchParams) === null || _b === void 0 ? void 0 : _b.campaignId,
        props.currentStudent.advisor,
        props.currentStudent.coach,
        scheduleRestrictionBypassed,
        setServiceWithAvailability,
    ]);
    const [date, setDate] = useState(moment());
    // Track service ID to detect changes
    const prevServiceIdRef = React.useRef(null);
    // Reset calendar to current date when service changes
    useEffect(() => {
        var _a, _b;
        const serviceIdForCalendar = (_b = (_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        if (prevServiceIdRef.current !== null &&
            prevServiceIdRef.current !== serviceIdForCalendar) {
            // Service changed - reset calendar to today
            setDate(moment());
        }
        prevServiceIdRef.current = serviceIdForCalendar;
    }, [(_c = formSelections.service) === null || _c === void 0 ? void 0 : _c.id]);
    // Track if we've already processed the current slots to avoid repeated updates
    // whenever the slots change, if our selected date is out of range, pick the first date available
    // and sync the calendar month with the new date
    useEffect(() => {
        if (slotsState.data && slotsState.data.length !== 0) {
            const slots = slotsState.data;
            const min = moment(slots[0].from).startOf("day");
            const max = moment(slots[slots.length - 1].to).endOf("day");
            if (!moment(date).isBetween(min, max, "day", "[]")) {
                setDate(min);
                // Sync formSelections month/year with the new date to keep calendar and API in sync
                const newMonth = min.month() + 1;
                const newYear = min.year();
                setFormSelections((fs) => {
                    // Only update if month actually changed to avoid unnecessary re-renders
                    if (fs.monthIndex !== newMonth || fs.year !== newYear) {
                        // Skip next fetch since this is just syncing the calendar display
                        skipNextFetchRef.current = true;
                        return Object.assign(Object.assign({}, fs), { monthIndex: newMonth, year: newYear, afterDayInMonth: newMonth === new Date().getMonth() + 1
                                ? new Date().getDate()
                                : null });
                    }
                    return fs;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slotsState.data]); // Only depend on slotsState.data to avoid loops
    const saveButtonRef = React.useRef(null);
    const [dependencyErrorMessage, setDependencyErrorMessage] = useState(null);
    const filteredSlots = React.useMemo(() => {
        const selectedDate = moment(date).startOf("day");
        const lastDateAfterSelectedDate = moment(selectedDate)
            .add(2, "days")
            .endOf("day");
        if (slotsState.data == null || slotsState.data.length === 0) {
            return [];
        }
        const slots = slotsState.data;
        // if the selected date is earlier than the first slot or later than the last slot, return nothing
        if (lastDateAfterSelectedDate.endOf("day").unix() * 1000 <
            slots[0].from.getTime() ||
            selectedDate.startOf("day").unix() * 1000 >
                slots[slots.length - 1].to.getTime()) {
            return [];
        }
        // FYI we don't handle date ranges spanning multiple months
        return slots.filter((s) => moment(s.from).isSameOrAfter(selectedDate) &&
            moment(s.to).isBefore(lastDateAfterSelectedDate));
    }, [date, slotsState.data]);
    const termsToRepresentState = useTermsToRepresent();
    return (_jsxs(_Fragment, { children: [_jsx(ServiceResult, { state: termsToRepresentState, children: ({ termToRepresentAdvisor, termToRepresentCoach }) => {
                    var _a;
                    return (_jsxs(BasicFormFormat, { defaultValue: () => __awaiter(void 0, void 0, void 0, function* () {
                            var _b, _c, _d, _e, _f;
                            if (props.searchParams) {
                                const stableSearchParams = yield parseSearchParamsToDefaultValues(props.searchParams);
                                // Determine the effective offering (history takes precedence)
                                const effectiveOffering = (_b = historyFormSelections.offering) !== null && _b !== void 0 ? _b : stableSearchParams.offering;
                                // Determine if staff should be populated based on offering restrictions
                                // Staff should NOT be populated if:
                                // 1. showStaffNames is false (hide staff names)
                                // 2. requireAdvisorScheduling is true AND not bypassed for this service AND student has assignable staff
                                // Campaign visits bypass all restrictions
                                const shouldPopulateStaff = (() => {
                                    var _a;
                                    // If no service selected, allow staff from URL
                                    if (!effectiveOffering)
                                        return true;
                                    // Campaign visits bypass all restrictions
                                    if ((_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.campaignId)
                                        return true;
                                    // If hide staff names, don't populate
                                    if (!effectiveOffering.showStaffNames)
                                        return false;
                                    // If advisor scheduling required and not bypassed, don't populate
                                    // UNLESS student has no assignable staff (no restriction applies)
                                    if (effectiveOffering.requireAdvisorScheduling &&
                                        !(scheduleRestrictionBypassed &&
                                            bypassedForServiceId === effectiveOffering.id)) {
                                        // Check if student has assignable staff
                                        const hasAdvisor = !!props.currentStudent.advisor;
                                        const hasCoach = effectiveOffering.allowCoachAsAlternative &&
                                            !!props.currentStudent.coach;
                                        const hasAssignableStaff = hasAdvisor || hasCoach;
                                        // If student has no assignable staff, allow staff population (no restriction)
                                        if (!hasAssignableStaff)
                                            return true;
                                        return false;
                                    }
                                    return true;
                                })();
                                return Object.assign(Object.assign({}, stableSearchParams), { offering: effectiveOffering, locations: (_c = historyFormSelections.locations) !== null && _c !== void 0 ? _c : stableSearchParams.locations, offices: (_d = historyFormSelections.offices) !== null && _d !== void 0 ? _d : stableSearchParams.offices, staff: shouldPopulateStaff
                                        ? (_e = historyFormSelections.staff) !== null && _e !== void 0 ? _e : stableSearchParams.staff
                                        : [], modalities: (_f = historyFormSelections.modalities) !== null && _f !== void 0 ? _f : stableSearchParams.modalities });
                            }
                            // For history-only restoration, also check advisor restrictions
                            // Note: If we reach here, props.searchParams is null/undefined, so no campaign check needed
                            const effectiveOffering = historyFormSelections.offering;
                            const shouldPopulateStaffFromHistory = (() => {
                                if (!effectiveOffering)
                                    return true;
                                if (!effectiveOffering.showStaffNames)
                                    return false;
                                if (effectiveOffering.requireAdvisorScheduling &&
                                    !(scheduleRestrictionBypassed &&
                                        bypassedForServiceId === effectiveOffering.id)) {
                                    // Check if student has assignable staff
                                    const hasAdvisor = !!props.currentStudent.advisor;
                                    const hasCoach = effectiveOffering.allowCoachAsAlternative &&
                                        !!props.currentStudent.coach;
                                    const hasAssignableStaff = hasAdvisor || hasCoach;
                                    // If student has no assignable staff, allow staff population (no restriction)
                                    if (!hasAssignableStaff)
                                        return true;
                                    return false;
                                }
                                return true;
                            })();
                            return Object.assign(Object.assign({}, historyFormSelections), { staff: shouldPopulateStaffFromHistory
                                    ? historyFormSelections.staff
                                    : [] });
                        }), handleSubmit: (form) => __awaiter(void 0, void 0, void 0, function* () {
                            var _g, _h;
                            // Check if the service has changed
                            const isServiceChanged = ((_g = form.offering) === null || _g === void 0 ? void 0 : _g.id) !== ((_h = formSelections.service) === null || _h === void 0 ? void 0 : _h.id);
                            const getAvailableStaffForService = () => {
                                var _a, _b, _c;
                                // Staff users are not subject to advisor scheduling restrictions
                                if (props.isCurrentUserStaff) {
                                    return form.staff;
                                }
                                // Campaign visits bypass all restrictions to ensure campaign fulfillment
                                if ((_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.campaignId) {
                                    return form.staff;
                                }
                                // If advisor scheduling is not required, use the user's selection
                                if (!((_b = form.offering) === null || _b === void 0 ? void 0 : _b.requireAdvisorScheduling)) {
                                    return form.staff;
                                }
                                // Check if the TARGET service (the one we're switching to) is already bypassed
                                // This handles the case when user switches back to a previously bypassed service
                                const isTargetServiceBypassed = ((_c = form.offering) === null || _c === void 0 ? void 0 : _c.id) != null &&
                                    bypassedServiceIds.includes(form.offering.id);
                                // If fallback has been triggered for this service, allow normal staff selection
                                if (isTargetServiceBypassed) {
                                    return form.staff;
                                }
                                const forcedStaff = [];
                                const addStaff = (person) => {
                                    if (person) {
                                        forcedStaff.push(person);
                                    }
                                };
                                // Add advisor
                                addStaff(props.currentStudent.advisor);
                                // Add coach if allowed as alternative
                                if (form.offering.allowCoachAsAlternative) {
                                    addStaff(props.currentStudent.coach);
                                }
                                // If student has no assignable staff, allow normal selection (no restriction)
                                if (forcedStaff.length === 0) {
                                    return form.staff;
                                }
                                return forcedStaff;
                            };
                            setFormSelections((fs) => {
                                var _a, _b, _c, _d;
                                return (Object.assign(Object.assign(Object.assign({}, fs), { staff: (_a = getAvailableStaffForService()) !== null && _a !== void 0 ? _a : [], service: form.offering, offices: (_b = form.offices) !== null && _b !== void 0 ? _b : [], modalities: (_c = form.modalities) !== null && _c !== void 0 ? _c : [], campuses: (_d = form.locations) !== null && _d !== void 0 ? _d : [] }), (isServiceChanged
                                    ? {
                                        monthIndex: new Date().getMonth() + 1,
                                        year: new Date().getFullYear(),
                                        afterDayInMonth: new Date().getDate(),
                                    }
                                    : {})));
                            });
                        }), saveButtonRef: saveButtonRef, errorMessagePosition: "bottom", disableSaveButton: !!dependencyErrorMessage, hideButtons: true, children: [_jsx(MeetingDependencyForm, { initialDependencies: props.dependencies, setDependencyErrorMessage: setDependencyErrorMessage, saveButtonRef: saveButtonRef, slotsState: slotsState, isCurrentUserStaff: props.isCurrentUserStaff, termToRepresentAdvisor: termToRepresentAdvisor, termToRepresentCoach: termToRepresentCoach, scheduleRestrictionBypassed: scheduleRestrictionBypassed, bypassedForServiceId: bypassedForServiceId, currentStudent: props.currentStudent, isFromCampaign: !!((_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.campaignId), onClear: () => {
                                    // Don't reset bypass state - just let form clear naturally
                                    // Bypass state should persist so popup doesn't show again
                                } }), dependencyErrorMessage && (_jsx(AlertError, { className: "mt-2", message: dependencyErrorMessage }))] }));
                } }), (slotsState.isLoading ||
                slotsState.wasSuccessful ||
                slotsState.hasError) && _jsx("hr", { className: "mb-5" }), _jsx(ServiceResult, { state: slotsState, loader: Loaders.overlay(), children: (slots) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    return (_jsxs("div", { className: "row", children: [_jsx("div", { className: "col-12 col-lg-4", children: _jsx("center", { children: _jsx(MeetingSchedulerCalendar
                                    // Key forces calendar to re-mount on service change
                                    , { highlightedDates: ListService.unique(slots.map((s) => DateService.getStartOfDay(s.from)), (d) => d.getTime()), value: date, onChange: setDate, onMonthChange: (newMonth, newYear) => {
                                            const currentDateMonth = moment(date).month() + 1;
                                            const currentDateYear = moment(date).year();
                                            setFormSelections((fs) => (Object.assign(Object.assign({}, fs), { monthIndex: newMonth, 
                                                // if the newMonth is the current month, don't fetch slots for the entire month.  Just today and after
                                                afterDayInMonth: newMonth === new Date().getMonth() + 1
                                                    ? new Date().getDate()
                                                    : null, year: newYear })));
                                            // Keep date in sync with visible month to avoid empty filteredSlots when navigating back
                                            if (currentDateMonth !== newMonth ||
                                                currentDateYear !== newYear) {
                                                const isCurrentMonth = newMonth === new Date().getMonth() + 1 &&
                                                    newYear === new Date().getFullYear();
                                                setDate(isCurrentMonth
                                                    ? moment()
                                                    : moment({
                                                        year: newYear,
                                                        month: newMonth - 1,
                                                        date: 1,
                                                    }));
                                            }
                                        } }, `calendar-${(_b = (_a = formSelections.service) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "none"}`) }) }), _jsx("div", { className: "col-12 col-lg-8 mt-lg-0 mt-5", children: formSelections.service && (_jsx(Slots, { slots: filteredSlots, service: formSelections.service, studentId: (_c = props.searchParams) === null || _c === void 0 ? void 0 : _c.studentId, emptyMessage: `No available times in ${new Date(0, formSelections.monthIndex - 1).toLocaleString("default", {
                                        month: "long",
                                    })}.`, formSelections: formSelections, setFormSelections: setFormSelections, isStaff: props.isCurrentUserStaff, scheduleRestrictionBypassed: scheduleRestrictionBypassed, bypassedForServiceId: bypassedForServiceId, setBypassedForServiceId: setBypassedForServiceId, serviceWithAvailability: serviceWithAvailability, setServiceWithAvailability: setServiceWithAvailability, currentStudent: props.currentStudent, isFromCampaign: !!((_d = props.searchParams) === null || _d === void 0 ? void 0 : _d.campaignId), termToRepresentAdvisor: (_f = (_e = termsToRepresentState.data) === null || _e === void 0 ? void 0 : _e.termToRepresentAdvisor) !== null && _f !== void 0 ? _f : "Advisor", termToRepresentCoach: (_h = (_g = termsToRepresentState.data) === null || _g === void 0 ? void 0 : _g.termToRepresentCoach) !== null && _h !== void 0 ? _h : "Coach", availabilityCheckDone: availabilityCheckDone, hasAvailabilityInFallbackWindow: hasAvailabilityInFallbackWindow, setDate: setDate })) })] }));
                } })] }));
};
const MeetingScheduleComponent = (props) => {
    var _a;
    const [meetingDependencyState, getMeetingDependencies] = useService(MeetingAvailabilityService.getDependencyList);
    const [appointmentCampaignState, getAppointmentCampaignInfo] = useService((id) => __awaiter(void 0, void 0, void 0, function* () {
        if (!id) {
            return null;
        }
        return CampaignService.getAppointmentCampaignLiteInfo(id);
    }));
    useEffect(() => {
        getAppointmentCampaignInfo(props.searchParams.campaignId);
        getMeetingDependencies(props.searchParams.campaignId);
    }, [
        getAppointmentCampaignInfo,
        getMeetingDependencies,
        props.searchParams.campaignId,
    ]);
    const [isStaffState, loadIsStaff] = useService(RoleService.currentUserIsStaff);
    const [currentStudentState, loadCurrentStudent] = useService(PersonService.getFullSanitizedByAffiliationId);
    React.useEffect(() => {
        var _a;
        loadIsStaff();
        loadCurrentStudent((_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.studentId);
    }, [loadIsStaff, loadCurrentStudent, (_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.studentId]);
    return (_jsx(ContentLayout, { paddingTop: true, notFluid: true, paddingBottom: true, className: "fullMinHeightMinusPrimaryNav", children: _jsx(CustomCard, { className: "mt-4", title: "Schedule A Meeting", children: _jsx("div", { className: "card-body", children: _jsx("div", { className: "mb-4", children: _jsx(ServiceResult, { state: appointmentCampaignState, stillRenderChildWhenNoData: true, children: (appointmentCampaign) => (_jsxs(_Fragment, { children: [appointmentCampaign && (_jsx(AppointmentCampaignDetails, { appointmentCampaign: appointmentCampaign })), _jsx(ServiceResult, { state: meetingDependencyState, children: (dependencies) => (_jsx(ServiceResult, { state: isStaffState, children: (isStaff) => (_jsx(ServiceResult, { state: currentStudentState, children: (currentStudent) => (_jsx(SchedulerContent, { dependencies: dependencies, searchParams: props.searchParams, minDate: appointmentCampaign === null || appointmentCampaign === void 0 ? void 0 : appointmentCampaign.startDate, maxDate: appointmentCampaign === null || appointmentCampaign === void 0 ? void 0 : appointmentCampaign.endDate, isCurrentUserStaff: isStaff, currentStudent: currentStudent })) })) })) })] })) }) }) }) }) }));
};
const MeetingSchedule = () => {
    const params = useParams();
    const safeSearchParams = MEETINGS_SCHEDULE_SEARCH_PARAMS.safeParse(params);
    if (!safeSearchParams.success) {
        return (_jsx(ContentLayout, { notFluid: true, paddingBottom: true, paddingTop: true, className: "fullMinHeightMinusPrimaryNav", children: _jsx(AlertError, { message: "Invalid request" }) }));
    }
    return _jsx(MeetingScheduleComponent, { searchParams: safeSearchParams.data });
};
export default MeetingSchedule;
